/** Overdue versus stale, as two lists (ticket body, plan §8). `facts.overdue`
 * / `facts.stale` are key arrays into `facts.commitments`. Actionable via a
 * compact per-row menu; an item disappears once its key is in
 * `overview.suppressedKeys` (the server's `isHelmItemSuppressed` verdict) —
 * an active Defer/Waiting-on hides it, an expired one does not.
 *
 * A stale row's open `roster_close` proposal (R5) is decided on the row itself
 * (A7Y-4733, "each row in one place"): Approve/Reject sit beside the row's
 * menu, and `Proposals` leaves those proposals out of its Open list. */
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import type { HelmActionInput, HelmCommitment, HelmProposal } from '@a7y/platform-health/helm';
import { ItemActionsMenu } from '../ItemActionsMenu';
import { useHelmReadOnly } from '../readOnly';
import { isStaleCloseProposal } from '../staleRows';
import { HELM_TABLE_GUTTER, HelmEmpty, HelmSection } from './HelmSection';
import { RowName } from '../RowName';
import { CRM_STALE_AFTER_DAYS } from '@a7y/types';

function List({
  title,
  items,
  proposals = [],
  onAction,
}: {
  title: string;
  items: HelmCommitment[];
  /** Open close proposals to decide on a row; empty on the Overdue list. */
  proposals?: HelmProposal[];
  onAction: (input: HelmActionInput) => void;
}) {
  const readOnly = useHelmReadOnly();
  const proposalFor = (c: HelmCommitment) =>
    proposals.find((p) => isStaleCloseProposal(p, new Set([c.key])));
  return (
    <HelmSection title={title} lineOn="none">
      {items.length === 0 ? (
        <HelmEmpty>none</HelmEmpty>
      ) : (
        <Table dense className={HELM_TABLE_GUTTER}>
          <TableHead>
            <TableRow>
              <TableHeader>Name</TableHeader>
              <TableHeader>Overdue</TableHeader>
              <TableHeader></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((c) => {
              const proposal = proposalFor(c);
              return (
                <TableRow key={c.key}>
                  <TableCell className="min-w-0 break-words">
                    <RowName commitment={c} />
                    {proposal && (
                      <span
                        data-testid="helm-stale-proposal-text"
                        className="block text-xs text-zinc-500"
                      >
                        HELM&apos;s proposal, not applied: {proposal.proposed}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-zinc-500">
                    {c.overdueDays != null ? `${c.overdueDays}d overdue` : ''}
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center justify-end gap-1.5">
                      {proposal && !readOnly && (
                        <>
                          <Button
                            outline
                            data-testid="helm-stale-proposal-approve"
                            onClick={() => onAction({ itemKey: proposal.key, kind: 'approve' })}
                          >
                            Approve
                          </Button>
                          <Button
                            outline
                            data-testid="helm-stale-proposal-reject"
                            onClick={() => onAction({ itemKey: proposal.key, kind: 'reject' })}
                          >
                            Reject
                          </Button>
                        </>
                      )}
                      <ItemActionsMenu itemKey={c.key} decision={c.decision} onAction={onAction} />
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </HelmSection>
  );
}

export function OverdueStale({
  commitments,
  overdue,
  stale,
  proposals,
  suppressedKeys,
  onAction,
}: {
  commitments: HelmCommitment[];
  overdue: string[];
  stale: string[];
  proposals: HelmProposal[];
  suppressedKeys: ReadonlySet<string>;
  onAction: (input: HelmActionInput) => void;
}) {
  const byKey = new Map(commitments.map((c) => [c.key, c] as const));
  const resolve = (keys: string[]) =>
    keys
      .map((key) => byKey.get(key))
      .filter((c): c is HelmCommitment => c != null && !suppressedKeys.has(c.key));

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      {/* A7Y-5210: the one stale threshold, shared with the Accounts page's "Stale only". */}
      <List
        title={`Overdue (1–${CRM_STALE_AFTER_DAYS} d)`}
        items={resolve(overdue)}
        onAction={onAction}
      />
      <List
        title={`Stale (over ${CRM_STALE_AFTER_DAYS} d)`}
        items={resolve(stale)}
        proposals={proposals}
        onAction={onAction}
      />
    </div>
  );
}
